import { useState, type FormEvent } from 'react';
import type { LoginResponse } from '@clarity/contract';
import {
  useLogin,
  useRequestVerify,
  useVerifyPhone,
  useRequestReset,
  useResetPassword,
} from '../queries/index.js';
import { useSession } from '../lib/session.js';
import { Logo } from '../components/Logo.js';

/**
 * Seeded dev accounts, shown on the login form ONLY in a dev build
 * (`import.meta.env.DEV`). Phone-first, mirroring `clarity-server`'s `pnpm
 * db:seed`. Click "Use …" to fill the form.
 */
const DEV_CREDENTIALS: ReadonlyArray<{ role: string; phone: string; password: string }> =
  import.meta.env.DEV
    ? [
        { role: 'admin', phone: '+962790000001', password: 'Admin!2026demo' },
        { role: 'client', phone: '+962790000002', password: 'Client!2026demo' },
      ]
    : [];

type Mode = 'signin' | 'verify' | 'reset';

/**
 * The unauthenticated entry point (Req 6.5), PHONE-FIRST (clarity-workspace-
 * users). Sign in with phone + password. Two side flows backed by a WhatsApp
 * one-time code:
 * - VERIFY: a newly-added user activates their phone (request code → enter it),
 *   landing straight in the app on success.
 * - RESET: a forgotten password (request code → enter code + new password).
 *
 * All three share the typed `auth.*` mutations; on success the returned
 * `{ token, user }` is stored in the session, flipping the shell to the app.
 */
export function Login() {
  const { signIn } = useSession();
  const [mode, setMode] = useState<Mode>('signin');
  const [phone, setPhone] = useState('');

  function onAuthed(res: LoginResponse) {
    signIn({ token: res.token, user: res.user });
  }

  return (
    <main className="auth-screen">
      <div className="card auth-card">
        <div className="auth-brand">
          <Logo className="logo" />
          <span className="wordmark">Clarity</span>
        </div>
        <p className="auth-tagline">
          Restaurant profit, made clear. Every delivery order, payout and statement reconciled,
          with AI recommendations on what to do next.
        </p>

        {mode === 'signin' && (
          <SignInForm
            phone={phone}
            setPhone={setPhone}
            onAuthed={onAuthed}
            onVerifyNeeded={() => setMode('verify')}
            onForgot={() => setMode('reset')}
          />
        )}
        {mode === 'verify' && (
          <VerifyForm phone={phone} setPhone={setPhone} onAuthed={onAuthed} onBack={() => setMode('signin')} />
        )}
        {mode === 'reset' && (
          <ResetForm phone={phone} setPhone={setPhone} onAuthed={onAuthed} onBack={() => setMode('signin')} />
        )}

        {DEV_CREDENTIALS.length > 0 && mode === 'signin' && (
          <aside className="dev-credentials" aria-label="Development login credentials">
            <p className="dev-credentials__title">Dev credentials</p>
            <ul>
              {DEV_CREDENTIALS.map((c) => (
                <li key={c.phone}>
                  <button
                    type="button"
                    className="dev-credentials__fill"
                    onClick={() => setPhone(c.phone)}
                  >
                    Use {c.role}
                  </button>
                  <code>
                    {c.phone} / {c.password}
                  </code>
                </li>
              ))}
            </ul>
          </aside>
        )}
      </div>
    </main>
  );
}

function PhoneField({ phone, setPhone }: { phone: string; setPhone: (v: string) => void }) {
  return (
    <label>
      Phone
      <input
        type="tel"
        name="phone"
        inputMode="tel"
        autoComplete="username"
        placeholder="+962790000000"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        required
      />
    </label>
  );
}

function SignInForm({
  phone,
  setPhone,
  onAuthed,
  onVerifyNeeded,
  onForgot,
}: {
  phone: string;
  setPhone: (v: string) => void;
  onAuthed: (r: LoginResponse) => void;
  onVerifyNeeded: () => void;
  onForgot: () => void;
}) {
  const login = useLogin();
  const [password, setPassword] = useState('');
  const [rejected, setRejected] = useState(false);

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setRejected(false);
    login.mutate(
      { body: { phone, password } },
      {
        onSuccess: (res) => (res.status === 200 ? onAuthed(res.body) : setRejected(true)),
      },
    );
  }

  return (
    <form onSubmit={onSubmit} aria-label="Sign in">
      <PhoneField phone={phone} setPhone={setPhone} />
      <label>
        Password
        <input
          type="password"
          name="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
      </label>
      <button type="submit" disabled={login.isPending}>
        {login.isPending ? 'Signing in…' : 'Sign in'}
      </button>
      {(rejected || login.isError) && (
        <p role="alert" className="error">
          Sign in failed. Check your phone and password, or verify your phone if you haven&apos;t yet.
        </p>
      )}
      <div className="auth-links">
        <button type="button" className="linkish" onClick={onVerifyNeeded}>
          Verify a new phone
        </button>
        <button type="button" className="linkish" onClick={onForgot}>
          Forgot password?
        </button>
      </div>
    </form>
  );
}

/** Shared "enter the 6-digit code" sub-flow used by verify + reset. */
function CodeStep({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label>
      Verification code
      <input
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        placeholder="6-digit code"
        maxLength={6}
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 6))}
        required
      />
    </label>
  );
}

function VerifyForm({
  phone,
  setPhone,
  onAuthed,
  onBack,
}: {
  phone: string;
  setPhone: (v: string) => void;
  onAuthed: (r: LoginResponse) => void;
  onBack: () => void;
}) {
  const request = useRequestVerify();
  const verify = useVerifyPhone();
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState('');
  const [devCode, setDevCode] = useState<string | null>(null);

  function onRequest(e: FormEvent) {
    e.preventDefault();
    request.mutate(
      { body: { phone } },
      {
        onSuccess: (res) => {
          if (res.status === 200) {
            setSent(true);
            setDevCode(res.body.devCode);
          }
        },
      },
    );
  }

  function onVerify(e: FormEvent) {
    e.preventDefault();
    verify.mutate(
      { body: { phone, code } },
      { onSuccess: (res) => res.status === 200 && onAuthed(res.body) },
    );
  }

  return (
    <form onSubmit={sent ? onVerify : onRequest} aria-label="Verify phone">
      <h3 className="auth-step-title">Verify your phone</h3>
      <PhoneField phone={phone} setPhone={setPhone} />
      {sent && <CodeStep value={code} onChange={setCode} />}
      <button type="submit" disabled={request.isPending || verify.isPending}>
        {!sent
          ? request.isPending
            ? 'Sending…'
            : 'Send code'
          : verify.isPending
            ? 'Verifying…'
            : 'Verify'}
      </button>
      {sent && devCode && (
        <p role="status" className="muted">
          Dev: your code is <code>{devCode}</code> (WhatsApp not configured).
        </p>
      )}
      {sent && !devCode && !verify.isError && (
        <p role="status" className="muted">
          If that phone has an account, a code is on its way via WhatsApp.
        </p>
      )}
      {verify.isError && (
        <p role="alert" className="error">
          That code is invalid or expired. Request a new one.
        </p>
      )}
      <div className="auth-links">
        <button type="button" className="linkish" onClick={onBack}>
          Back to sign in
        </button>
      </div>
    </form>
  );
}

function ResetForm({
  phone,
  setPhone,
  onAuthed,
  onBack,
}: {
  phone: string;
  setPhone: (v: string) => void;
  onAuthed: (r: LoginResponse) => void;
  onBack: () => void;
}) {
  const request = useRequestReset();
  const reset = useResetPassword();
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [devCode, setDevCode] = useState<string | null>(null);

  function onRequest(e: FormEvent) {
    e.preventDefault();
    request.mutate(
      { body: { phone } },
      {
        onSuccess: (res) => {
          if (res.status === 200) {
            setSent(true);
            setDevCode(res.body.devCode);
          }
        },
      },
    );
  }

  function onReset(e: FormEvent) {
    e.preventDefault();
    reset.mutate(
      { body: { phone, code, newPassword } },
      { onSuccess: (res) => res.status === 200 && onAuthed(res.body) },
    );
  }

  return (
    <form onSubmit={sent ? onReset : onRequest} aria-label="Reset password">
      <h3 className="auth-step-title">Reset your password</h3>
      <PhoneField phone={phone} setPhone={setPhone} />
      {sent && (
        <>
          <CodeStep value={code} onChange={setCode} />
          <label>
            New password
            <input
              type="password"
              autoComplete="new-password"
              placeholder="at least 8 characters"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={8}
            />
          </label>
        </>
      )}
      <button
        type="submit"
        disabled={request.isPending || reset.isPending || (sent && newPassword.length < 8)}
      >
        {!sent
          ? request.isPending
            ? 'Sending…'
            : 'Send code'
          : reset.isPending
            ? 'Resetting…'
            : 'Set new password'}
      </button>
      {sent && devCode && (
        <p role="status" className="muted">
          Dev: your code is <code>{devCode}</code> (WhatsApp not configured).
        </p>
      )}
      {sent && !devCode && !reset.isError && (
        <p role="status" className="muted">
          If that phone has an account, a reset code is on its way via WhatsApp.
        </p>
      )}
      {reset.isError && (
        <p role="alert" className="error">
          That code is invalid or expired. Request a new one.
        </p>
      )}
      <div className="auth-links">
        <button type="button" className="linkish" onClick={onBack}>
          Back to sign in
        </button>
      </div>
    </form>
  );
}
